import styles from "./styles.module.scss";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../../components/Button";
import { CardInfo } from "../components/CardInfo";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import { ColDef, IGetRowsParams } from "ag-grid-community";
import { AddContractedPerfil } from "../components/AddContractedPerfil";
import { getProjectBudgetsByProjectId } from "../../../services/api/ProjectBudget/ProjectBudgetService";
import { IProjectBudgetResponse } from "../../../@types/project/project-budget.type";
import { IApiResponse } from "../../../@types/api-response.type";
import dayjs from "dayjs";
import { formatCurrency } from "../../../utils/formatCurrency";
import { HistoryHiredModal } from "../components/HistoryHiredModal";
import InputFileUpload from "../components/InputFileUpload";
import { storageUserGet } from "../../../storage/storageUser";

function addColumnStartEndDate() {
  const startDateColumn = {
    headerName: "Início",
    field: "startDate",
    columnGroupShow: "closed",
    cellRenderer: (params: CustomCellRendererProps) => {
      return dayjs(params.value).format("MMM/YY");
    },
  };

  const endDateColumn = {
    headerName: "Término",
    field: "endDate",
    columnGroupShow: "closed",
    cellRenderer: (params: CustomCellRendererProps) => {
      return dayjs(params.value).format("MMM/YY");
    },
  };

  return [startDateColumn, endDateColumn];
}

const columnsDefault: ColDef[] = [
  {
    field: "id",
    headerName: "ID",
    hide: true,
  },
  {
    field: "positionName",
    headerName: "Perfil",
    flex: 1,
    minWidth: 200,
    pinned: "left",
  },
  {
    field: "seniorityLevelName",
    headerName: "Nivel",
    flex: 1,
    minWidth: 100,
    pinned: "left",
  },
  {
    field: "hoursTotal",
    headerName: "Total de Horas",
    flex: 1,
    minWidth: 150,
    cellRenderer: (params: CustomCellRendererProps) => {
      return <>{params.value ? params.value + " h" : "-"}</>;
    },
  },
  {
    field: "budgetTotal",
    headerName: "Custo Total",
    flex: 1,
    minWidth: 200,
    cellRenderer: (params: CustomCellRendererProps) => {
      return params.value ? formatCurrency(params.value) : "-";
    },
  },
  {
    field: "salePriceTotal",
    headerName: "Venda Total",
    flex: 1,
    minWidth: 200,
    cellRenderer: (params: CustomCellRendererProps) => {
      return params.value ? formatCurrency(params.value) : "-";
    },
  },
  {
    headerName: "Período",
    children: addColumnStartEndDate(),
  },
];

interface HiredProjectProps {
  projectId: string;
}

export const HiredProject: React.FC<HiredProjectProps> = ({ projectId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [columns, setColumns] = useState(columnsDefault);
  const [obj, setObj] = useState<IProjectBudgetResponse>();
  const [idEdit, setIdEdit] = useState<string>("");
  const [isImported, setIsImported] = useState<boolean>(false);

  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const [openModalHistory, setOpenModalHistory] = useState(false);
  const handleOpenHistoryModal = () => setOpenModalHistory(true);
  const handleCloseHistoryModal = () => setOpenModalHistory(false);

  const user = storageUserGet();

  const addColumnMonths = (params) => {
    const startDate = dayjs(
      params.reduce((a, b) => {
        return new Date(a.startDate) < new Date(b.startDate) ? a : b;
      }).startDate
    );

    const endDate = dayjs(
      params.reduce((a, b) => {
        return new Date(a.endDate) > new Date(b.endDate) ? a : b;
      }).endDate
    );

    const column = addColumnStartEndDate();

    const monthDiff = endDate.diff(startDate, "month") + 1;
    for (let index = 0; index < monthDiff; index++) {
      const month = startDate.add(index, "month");
      const monthAndYear = month.format("MMM") + " / " + month.format("YY");

      const newColumn = {
        field: monthAndYear,
        minWidth: 75,
        columnGroupShow: "open",
        cellRenderer: (row: CustomCellRendererProps) => {
          const columnValue = row.data.projectBudgetMonthlies.find(
            (x) =>
              x.month === parseInt(month.format("MM")) &&
              x.year === parseInt(month.format("YYYY"))
          );
          return columnValue && columnValue.workingHours
            ? `${columnValue.workingHours} h`
            : `${0} h`;
        },
      };
      column.push(newColumn);
    }
    columnsDefault[6].children = column;
    gridRef.current!.api.setGridOption("columnDefs", columnsDefault);
  };

  useEffect(() => {
    loadInfo();
  }, []);

  useEffect(() => {
    if(isImported){
      loadInfo();
      setIsImported(false);
    }
  }, [isImported]);
  
  useEffect(() => {
    if (!openModal) loadInfo();
  }, [openModal]);

  const loadInfo = async () => {
    const response = (await getProjectBudgetsByProjectId(
      projectId
    )) as IApiResponse<IProjectBudgetResponse>;
    setObj(response.result);
  };

  const updatePage = (id) => {
    setIdEdit(id);
    handleOpenModal();
  };

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      try {
        if (obj) {
          if (obj && obj.projectBudgets.length > 0)
            addColumnMonths(obj?.projectBudgets);

          params.successCallback(obj.projectBudgets, obj.projectBudgets.length);
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      }
    },
  });

  return (
    <div className={styles.hired}>
      <div className={styles.container}>
        <h3 className={styles.title}>Contratado</h3>
        <div className={styles.divButtons}>
          <InputFileUpload projectId={projectId} userId={user.id} setIsImported={setIsImported} />
          <Button
            content="Ver Historico"
            onClick={() => {
              handleOpenHistoryModal();
            }}
            className={styles.btnAdd}
          />
          <Button
            content="Adicionar Perfil"
            onClick={() => {
              setIdEdit("");
              handleOpenModal();
            }}
            className={styles.btnAdd}
          />
        </div>
      </div>

      <div className={styles.infoContainer}>
        <CardInfo
          text="Horas Contratadas"
          value={obj ? obj.hoursTotal : 0}
          type="hour"
          icon="AccessTimeIcon"
        />
        <CardInfo
          text="Custo Mão de Obra"
          value={obj ? obj.budgetTotal : 0}
          type="money"
          icon="MoneyOffIcon"
        />
        <CardInfo
          text="Venda Mão de Obra"
          value={obj ? obj.salePriceTotal : 0}
          type="money"
          icon="AttachMoneyIcon"
        />
        <CardInfo
          text="Margem Direta"
          value={obj ? obj.profitMargin : 0}
          type="money"
          icon="ShowChartIcon"
        />
      </div>
      <div className={styles.dataGridContainer}>
        <AgGridReact
          ref={gridRef}
          theme={themeGrid}
          localeText={localeText}
          loading={loading}
          columnDefs={columns}
          overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
          defaultColDef={{
            sortable: true,
            editable: false,
            filter: true,
            floatingFilter: true,
          }}
          suppressCellFocus={true}
          rowStyle={{ cursor: "pointer" }}
          onRowClicked={(row) => updatePage(row.data.id)}
          cacheBlockSize={50}
          rowModelType="infinite"
          datasource={getDataSource()}
          animateRows={true}
        />
      </div>
      {openModal && (
        <AddContractedPerfil
          projectId={projectId}
          id={idEdit}
          openModal={openModal}
          handleCloseModal={handleCloseModal}
        />
      )}
      {openModalHistory && (
        <HistoryHiredModal
          projectId={projectId}
          id={idEdit}
          openModal={openModalHistory}
          handleCloseModal={handleCloseHistoryModal}
        />
      )}
    </div>
  );
};
